import { Box, CircularProgress } from '@mui/material';
import React, { useContext } from 'react';
import { translate as t } from 'hooks/useTranslations';
import { TrainingInsightsCenterComplianceSummaryResponse, mapApiTrendToTrend } from '../_types';
import { useTrainingContext } from '../TrainingContext';
import { AppContext } from 'views/App/context';
import useTrainingInsightMetrics from '../_data_sources/useTrainingInsightMetrics';
import ChartInfo from '_lex/_atoms/ChartInfo';

const getChartInfoColor = (trend: string | null): 'success' | 'warning' | 'neutral' => {
  if (!trend) return 'neutral';
  switch (trend.toLowerCase()) {
    case 'increasing':
      return 'success';
    case 'decreasing':
      return 'warning';
    default:
      return 'neutral';
  }
};

const getChartInfoTrend = (trend: string | null): 'increasing' | 'decreasing' | 'neutral' | undefined => {
  if (!trend) return undefined;
  switch (trend.toLowerCase()) {
    case 'increasing':
      return 'increasing';
    case 'decreasing':
      return 'decreasing';
    default:
      return 'neutral';
  }
};

const getCardsData = (data: TrainingInsightsCenterComplianceSummaryResponse) => {
  const metrics = [
    {
      key: 'totalCompletions',
      value: data.totalCompletions?.value ?? 0,
      trend: mapApiTrendToTrend(data.totalCompletions?.trend ?? 'UNKNOWN'),
    },
    {
      key: 'totalHoursCompleted',
      value: data.totalHoursCompleted?.value ?? 0,
      trend: mapApiTrendToTrend(data.totalHoursCompleted?.trend ?? 'UNKNOWN'),
    },
    {
      key: 'nonCompliantUsers',
      value: data.nonCompliantUsers?.value ?? 0,
      trend: mapApiTrendToTrend(data.nonCompliantUsers?.trend ?? 'UNKNOWN'),
    },
    {
      key: 'credentialsToExpire',
      value: data.credentialsToExpire?.value ?? 0,
      trend: mapApiTrendToTrend(data.credentialsToExpire?.trend ?? 'UNKNOWN'),
    },
  ];

  return metrics.map((metric) => {
    const prefixKey = `reporting.insightsCenter.cards.${metric.key}`;
    return {
      title: t(`${prefixKey}.title`),
      description: t(`${prefixKey}.subTitle`),
      metric: metric.value,
      color: getChartInfoColor(metric.trend),
      trend: getChartInfoTrend(metric.trend),
      tooltip: t(`${prefixKey}.tooltip`),
    };
  });
};

export function InsightsMetrics() {
  const { currentOrganizationId } = useContext(AppContext);
  const { queryParams } = useTrainingContext();
  const trendsQuery = useTrainingInsightMetrics(currentOrganizationId, queryParams);

  const { data, isLoading, isError } = trendsQuery;
  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t('common.error.loadingData')}
      </Box>
    );
  }

  const cardData = getCardsData(data);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { sm: 'repeat(2, 1fr)', xs: 'repeat(1, 1fr)' },
        gap: '16px',
        width: '100%',
      }}
    >
      {cardData?.map((data, index) => (
        <ChartInfo
          key={`chart-info-${index}`}
          title={data.title}
          metric={data.metric}
          description={data.description}
          color={data.color}
          trend={data.trend}
          tooltip={data.tooltip}
        />
      ))}
    </Box>
  );
}

export default InsightsMetrics;
